import { useEffect, useRef, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";

import { ToastContainer } from "react-toastify";

import LoginPOS from "./LoginPOS";
import CompanySales from "./CompanySales";
import UltimosMovimientosPOS from "./UltimosMovimientosPOS";
import CompanyCashRegister from "./CompanyCashRegister";
import CompanyCashClosings from "./CompanyCashClosings";
import { CompanyBrandingProvider } from "./CompanyBrandingContext";

import {
  PermissionProvider,
  usePermissions,
} from "./PermissionContext";

import { api, clearPosSession } from "./api";

import "./POSLayout.css";

function validColor(value, fallback) {
  if (
    typeof value === "string" &&
    value.trim() &&
    !value.includes("var(") &&
    CSS.supports("color", value.trim())
  ) {
    return value.trim();
  }

  return fallback;
}

function POSHeader({ company, user, logout, refreshPermissions }) {
  const navigate = useNavigate();

  const [branding, setBranding] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();

    setBranding(null);
    setMenuOpen(false);

    api
      .get(
        `/companies/by-subdomain/${encodeURIComponent(company)}/branding`,
        { signal: controller.signal }
      )
      .then(({ data }) => {
        if (!controller.signal.aborted) {
          setBranding({ ...data, subdomain: company });
        }
      })
      .catch(() => {
        // La barra conserva sus valores predeterminados
        // si no se puede consultar la identidad visual.
      });

    return () => controller.abort();
  }, [company]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    function handleOutsideClick(event) {
      if (!menuRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    }

    function handleFocusOutside(event) {
      if (!menuRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    }

    document.addEventListener("pointerdown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("focusin", handleFocusOutside);

    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("focusin", handleFocusOutside);
    };
  }, [menuOpen]);

  const currentBranding =
    branding?.subdomain === company ? branding : null;

  const companyName =
    currentBranding?.commercial_name ||
    currentBranding?.company_name ||
    user.company_name ||
    company;

  const primary = validColor(
    currentBranding?.primary_color,
    "#1f5d7a"
  );

  const secondary = validColor(
    currentBranding?.secondary_color,
    primary
  );

  const userName = user.name || "Usuario";

  const initials = userName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

  function goToSales() {
    setMenuOpen(false);

    navigate(`/${encodeURIComponent(company)}/ventas`);
  }

  function updatePermissions() {
    setMenuOpen(false);
    triggerRef.current?.focus();
    refreshPermissions();
  }

  function handleLogout() {
    setMenuOpen(false);
    logout();
  }

  return (
    <header
      className="pos-topbar"
      style={{
        "--pos-nav-primary": primary,
        "--pos-nav-secondary": secondary,
      }}
    >
      <div className="pos-topbar-inner">
        <div className="pos-topbar-brand">
          <strong>NetLedger POS</strong>

          <span className="pos-topbar-divider" aria-hidden="true">
            /
          </span>

          <span className="pos-topbar-company" title={companyName}>
            {companyName}
          </span>
        </div>
        <nav className="pos-topbar-modules" aria-label="Módulos del POS">
          <NavLink
            to={`/${encodeURIComponent(company)}/ventas`}
            className={({ isActive }) =>
              `pos-module-link${isActive ? " pos-module-active" : ""}`
            }
            aria-label="Ventas"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 3h2l2.4 12h11.2l2-8H6" />
              <path d="M8 15l-1 3h12" />
              <circle cx="9" cy="21" r="1" />
              <circle cx="18" cy="21" r="1" />
            </svg>

            <span className="pos-module-tooltip" aria-hidden="true">
              Ventas
            </span>
          </NavLink>
          <NavLink
            to={`/${encodeURIComponent(company)}/caja`}
            className={({ isActive }) =>
              `pos-module-link${isActive ? " pos-module-active" : ""}`
            }
            aria-label="Caja"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="5" y="3" width="14" height="7" rx="2" />
              <path d="M8 6.5h5M7 10l-3 5v6h16v-6l-3-5" />
              <path d="M4 16h16M9 19h6M9 13h.01M12 13h.01M15 13h.01" />
            </svg>

            <span className="pos-module-tooltip" aria-hidden="true">
              Caja
            </span>
          </NavLink>

          <NavLink
            to={`/${encodeURIComponent(company)}/cierres-caja`}
            className={({ isActive }) =>
              `pos-module-link${isActive ? " pos-module-active" : ""}`
            }
            aria-label="Cierres de caja"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="4" y="5" width="16" height="16" rx="2" />
              <path d="M8 3v4M16 3v4M4 10h16M8 15l3 3 5-5" />
            </svg>

            <span className="pos-module-tooltip" aria-hidden="true">
              Cierres de caja
            </span>
          </NavLink>

          <NavLink
            to={`/${encodeURIComponent(company)}/ultimos-movimientos`}
            className={({ isActive }) =>
              `pos-module-link${isActive ? " pos-module-active" : ""}`
            }
            aria-label="Últimos movimientos"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 11a9 9 0 1 1 2.6 7.4" />
              <path d="M3 5v6h6M12 7v5l3 2" />
            </svg>

            <span className="pos-module-tooltip" aria-hidden="true">
              Últimos movimientos
            </span>
          </NavLink>
        </nav>

        <div className="pos-topbar-user" ref={menuRef}>
          <button
            ref={triggerRef}
            type="button"
            className="pos-user-trigger"
            aria-expanded={menuOpen}
            aria-controls="pos-user-dropdown"
            onClick={() => setMenuOpen((current) => !current)}
          >
            <span className="pos-user-avatar" aria-hidden="true">
              {initials}
            </span>

            <span className="pos-user-text">
              <strong>{userName}</strong>
              <small>Sesión POS</small>
            </span>

            <span
              className={`pos-user-chevron ${menuOpen ? "pos-user-chevron-open" : ""
                }`}
              aria-hidden="true"
            >
              ▾
            </span>
          </button>

          {menuOpen && (
            <div
              id="pos-user-dropdown"
              className="pos-user-dropdown"
            >
              <div className="pos-user-dropdown-heading">
                <strong>{userName}</strong>
                {user.email && <span>{user.email}</span>}
                <small>{companyName}</small>
              </div>

              <button
                type="button"
                className="pos-user-option"
                onClick={goToSales}
              >
                <span aria-hidden="true">▦</span>
                Ventas
              </button>

              <button
                type="button"
                className="pos-user-option"
                onClick={updatePermissions}
              >
                <span aria-hidden="true">↻</span>
                Actualizar permisos
              </button>

              <div className="pos-user-menu-divider" />

              <button
                type="button"
                className="pos-user-option pos-user-logout"
                onClick={handleLogout}
              >
                <span aria-hidden="true">↪</span>
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function ProtectedPOS() {
  const { company } = useParams();
  const navigate = useNavigate();

  const {
    user,
    error,
    loadingPermissions,
    refreshPermissions,
  } = usePermissions();

  if (loadingPermissions) {
    return (
      <div className="container py-5">
        Validando sesión…
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={refreshPermissions}
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!user || user.subdomain !== company) {
    return (
      <Navigate
        to={`/${encodeURIComponent(company)}`}
        replace
      />
    );
  }

  function logout() {
    clearPosSession(company);

    navigate(`/${encodeURIComponent(company)}`, {
      replace: true,
    });
  }

  return (
    <>
      <POSHeader
        company={company}
        user={user}
        logout={logout}
        refreshPermissions={refreshPermissions}
      />

      <main className="p-3 p-lg-4">
        <Outlet />
      </main>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <div className="container py-5">
              <h1 className="h3">NetLedger POS</h1>

              <p>
                Abre la dirección del POS de tu empresa.
                Ejemplo: /netsuperhelados
              </p>
            </div>
          }
        />

        <Route
          path="/:company"
          element={<LoginPOS />}
        />

        <Route
          path="/:company"
          element={
            <PermissionProvider>
              <CompanyBrandingProvider>
                <ProtectedPOS />
              </CompanyBrandingProvider>
            </PermissionProvider>
          }
        >
          <Route
            path="ventas"
            element={<CompanySales />}
          />

          <Route
            path="caja"
            element={<CompanyCashRegister />}
          />

          <Route
            path="cierres-caja"
            element={<CompanyCashClosings />}
          />

          <Route
            path="ultimos-movimientos"
            element={<UltimosMovimientosPOS />}
          />
        </Route>

        <Route
          path="*"
          element={
            <div className="container py-5">
              Página no encontrada.
            </div>
          }
        />
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={3500}
      />
    </BrowserRouter>
  );
}